// src/components/Modal/Sidebar.jsx
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { useAuthContext, useNotificationContext } from "../../context";
import { FaHome, FaEnvelope, FaSearch, FaUser, FaSignOutAlt, FaStar, FaEye, FaUsers, FaBullhorn, FaHeart } from 'react-icons/fa';

export default function Sidebar({ onSearch = () => {} }) {
  const { user, logoutHandler } = useAuthContext();
  const { chatUnreadCount } = useNotificationContext();
  const navigate = useNavigate();

//  const isGold = user?.membership_type === "gold" && user?.membership_status;

  const handleLogout = () => {
    Swal.fire({
      title: "Log out?",
      text: "You will be signed out of your account.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, log me out",
      cancelButtonText: "Cancel",
      reverseButtons: true,
      customClass: {
        popup: "rounded-xl shadow-xl p-4 max-w-sm",
        confirmButton:
          "bg-red-500 text-white px-4 py-2 rounded-full hover:bg-red-600 focus:outline-none",
        cancelButton:
          "bg-gray-100 text-gray-800 px-4 py-2 rounded-full hover:bg-gray-200 focus:outline-none",
        title: "text-lg font-semibold text-gray-800",
        htmlContainer: "text-sm text-gray-600",
      },
      buttonsStyling: false,
    }).then((result) => {
      if (result.isConfirmed) {
        logoutHandler();
        navigate("/login", { replace: true });
      }
    });
  };

  const comingSoon = () => {
    Swal.fire({
      icon: "info",
      title: "Coming soon!",
      text: "This feature is on its way—stay tuned!",
      confirmButtonText: "OK",
      customClass: {
        popup: "rounded-xl shadow-xl p-4 max-w-sm",
        confirmButton:
          "bg-blue-500 text-white px-4 py-2 rounded-full hover:bg-blue-600 focus:outline-none",
      },
      buttonsStyling: false,
    });
  };

  const scrollToTop = () =>
    window.scrollTo({ top: 0, behavior: "smooth" });

  const normalizeGender = (value) => {
    if (value === "Man") return "Male";
    if (value === "Woman") return "Female";
    return value;
  };  

  return (
    <aside className="hidden md:flex flex-col w-64 bg-white shadow-lg p-6">

      {/* Navigation */}
      <nav className="space-y-6 text-gray-800 flex-1">
        <div>
          <h4 className="text-xs font-semibold uppercase text-gray-500 mb-2">
            Activity
          </h4>
          <ul className="space-y-1">
  {/* Home */}
  <li>
    <Link to="/home" onClick={scrollToTop} className="flex items-center gap-2 hover:text-red-500">
      <FaHome />
      Home
    </Link>
  </li>

  {/* Inbox */}
  <li className="relative">
    <button
      onClick={() => {
        if (!user) {
          navigate("/login");
        } else {
          navigate("/inbox");
        }
        scrollToTop();
      }}      
      className="w-full text-left flex items-center gap-2 hover:text-red-500"
    >
      <FaEnvelope />
      Inbox
    </button>
    {user && chatUnreadCount > 0 && (
      <span className="absolute top-0 right-0 -mt-1 -mr-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
        !
      </span>
    )}
  </li>

  {/* Search */}
  <li>
    <button
      onClick={onSearch}
      className="w-full text-left flex items-center gap-2 hover:text-red-500"
    >
      <FaSearch />
      Search
    </button>
  </li>

  {/* Matches */}
  <button
  onClick={() => {
    if (!user) {
      navigate("/login");
      scrollToTop();
    } else {
      const matchesQuery = {
        ageRange: [18, 99],
        gender: normalizeGender(user.gender),
        lookingFor: normalizeGender(user.interestedIn),
        country: "",
        state: "",
        city: ""
      };

      localStorage.setItem("searchQuery", JSON.stringify(matchesQuery));
      navigate("/search", { state: matchesQuery });
      scrollToTop();
    }
  }}
  className="w-full text-left flex items-center gap-2 hover:text-red-500"
>
  <FaUsers />
  Matches
</button>

  {/* My Profile */}
  <li>
    <button
      onClick={() => {
        if (!user) {
          navigate("/login");
        } else {
          navigate("/profile");
        }
        scrollToTop();
      }}      
      className="w-full text-left flex items-center gap-2 hover:text-red-500"
    >
      <FaUser />
      My Profile
    </button>
  </li>

  {/* Favourites */}
  <li>
    <button
      onClick={() => {
        if (!user) {
          navigate("/login");
          scrollToTop();
        } else {
          comingSoon()
        }
      }}
      className="w-full text-left flex items-center gap-2 hover:text-red-500"
    >
      <FaStar />
      Favourites
    </button>
  </li>

  {/* Who's Viewed Me */}
  <li>
    <button
      onClick={() => {
        if (!user) {
          navigate("/login");
          scrollToTop();
        } else {
          comingSoon()
        }
      }}
      className="w-full text-left flex items-center gap-2 hover:text-red-500"
    >
      <FaEye />
      Who's Viewed Me
    </button>
  </li>
</ul>
        </div>

        <div>
         <h4 className="text-xs font-semibold uppercase text-gray-500 mb-2">
                       Community
                     </h4>
                     <ul className="space-y-1">
                       {/** All community items require login */}
                       {[
                         { label: "Events", icon: <FaBullhorn /> },
                         { label: "Message board", icon: <FaUsers /> },
                         { label: "Success stories", icon: <FaHeart /> },
                       ].map(({ label, icon }) => (
                         <li key={label}>
                           <button
                             onClick={() => {
                               if (!user) {
                                 navigate("/login");
                                 scrollToTop();
                               } else {
                                 comingSoon();
                               }
                             }}
                             className="w-full text-left hover:text-red-500 flex items-center space-x-2"
                           >
                             {icon}
                             <span>{label}</span>
                           </button>
                         </li>
                       ))}
                     </ul>
        </div>
         {/* Top buttons */}
       <div className="space-y-3 mb-8">
       {!user ? (
                   <>
                    <Link
                                    to="/signup"
                                    onClick= {scrollToTop}
                                    className="w-full text-center py-2 bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center justify-center space-x-2"
                                  >
                                    <FaUser />
                                    <span>Register now</span>
                                  </Link>
                                  <Link
                                    to="/login"
                                    onClick= {scrollToTop}
                                    className="w-full text-center py-2 bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center justify-center space-x-2"
                                  >
                                    <FaSignOutAlt />
                                    <span>Log in</span>
                                  </Link>
                   </>
                 ) : (
                  <button
                  onClick={handleLogout}
                  className="flex items-center justify-center gap-2 w-full py-2 bg-red-500 text-white rounded hover:bg-red-600"
                >
                  <FaSignOutAlt />
                  Logout
                </button>
        )}
      </div>
      </nav>
    </aside>
  );
}
